import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Header } from "@/pages/Landing";
import { DemoButton } from "@/components/DemoButton";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

const BrowserFrame = ({ src, alt, testId }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/50" data-testid={testId}>
    <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-rose-300" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
      <span className="ml-3 truncate font-mono text-[11px] text-slate-400">staffrotas.com</span>
    </div>
    <img src={src} alt={alt} loading="lazy" className="block w-full" />
  </div>
);

const Section = ({ s, i }) => (
  <section className={`py-16 lg:py-24 ${i % 2 ? "bg-slate-50" : "bg-white"}`} data-testid={`tour-section-${i}`}>
    <div className={`mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-[1fr_1.4fr] lg:px-12 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">{String(i + 1).padStart(2, "0")} · {s.eyebrow}</p>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">{s.title}</h2>
        <p className="mt-4 text-base text-slate-600">{s.body}</p>
        <ul className="mt-6 space-y-2 text-sm">
          {(s.bullets || []).map((b, j) => <li key={j} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{b}</li>)}
        </ul>
      </div>
      {s.image && <BrowserFrame src={s.image} alt={s.title} testId={`tour-image-${i}`} />}
    </div>
  </section>
);

export default function Tour() {
  usePageMeta("tour");
  const [c, setC] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/public/cms", { params: { key: "tour" } }).then((r) => setC(r.data)).catch(() => setFailed(true));
  }, []);
  if (failed) return <div className="grid min-h-screen place-items-center text-sm text-slate-500" data-testid="tour-error">The tour couldn't load. Please refresh the page.</div>;
  if (!c) return <div className="grid min-h-screen place-items-center" data-testid="tour-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" /></div>;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="grid-bg border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-8 lg:py-28">
          <p className="rise inline-block rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700">Product tour</p>
          <h1 className="rise d1 mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="tour-title">{c.title}</h1>
          <p className="rise d2 mx-auto mt-6 max-w-2xl text-base text-slate-600 sm:text-lg" data-testid="tour-subtitle">{c.subtitle}</p>
          <div className="rise d3 mt-10 flex flex-wrap justify-center gap-3">
            <DemoButton className="rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-700" testId="tour-hero-demo-btn" />
            <Link to="/signup" className="rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="tour-hero-signup-btn">Start free</Link>
          </div>
        </div>
      </section>
      {(c.sections || []).map((s, i) => <Section key={i} s={s} i={i} />)}
      <section className="bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="tour-cta-title">{c.cta_title}</h2>
          <p className="mt-4 text-slate-300">{c.cta_body}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <DemoButton className="rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-400" testId="tour-cta-demo-btn" />
            <Link to="/signup" className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100" data-testid="tour-cta-signup-btn">Start free <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
      <footer className="border-t border-slate-200 py-10"><div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12"><Logo /></div></footer>
    </div>
  );
}
